// teamCacheService.js
import { useAuth } from '../auth/AuthContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { schoolsApi } from '../utils/apiService';

export function useTeamCache() {
  const { user } = useAuth();
  const [activeTeam, setActiveTeam] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const lastFetchedUserId = useRef(null);

  useEffect(() => {
    const fetchTeamData = async () => {
      const userId = user?.uid;
      
      if (!userId || !user?.teams) {
        setAvailableTeams([]);
        setActiveTeam(null);
        setLoading(false);
        return;
      }

      // Prevent duplicate fetches for the same user
      if (lastFetchedUserId.current === userId) {
        return;
      }

      try {
        setLoading(true);
        
        // Fetch enriched team data from backend (includes team names from database)
        const response = await schoolsApi.getUserTeams();
        
        // The API returns { teams: [...], team_groups: [...], total_schools: N }
        const enrichedTeams = response.teams || [];
        const enrichedTeamGroups = response.team_groups || [];
        
        // Combine teams and team groups into availableTeams
        const allTeams = [
          ...enrichedTeams,
          ...enrichedTeamGroups
        ];
        
        // React handles state updates after unmount gracefully
        setAvailableTeams(allTeams);
        
        // Set active team to the first one
        if (allTeams.length > 0) {
          setActiveTeam(allTeams[0]);
        }
        
        // Mark this user as fetched
        lastFetchedUserId.current = userId;
        
      } catch (error) {
        console.error('Error fetching team data:', error);
        
        // Fallback to using Firestore data (without names)
        const teams = Object.entries(user.teams || {}).map(([id, data]) => ({
          id,
          ...data
        }));
        setAvailableTeams(teams);
        
        if (teams.length > 0) {
          setActiveTeam(teams[0]);
        }
        
        lastFetchedUserId.current = userId;
      } finally {
        setLoading(false);
      }
    };

    fetchTeamData();
  }, [user?.uid, user?.teams]);

  return {
    activeTeam,
    availableTeams,
    setActiveTeam,
    loading
  };
}

export function useTeamSchoolCache() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState('Initializing...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [teamsMetadata, setTeamsMetadata] = useState({ teams: [], team_groups: [] });
  
  // Use a ref to track if background loading is in progress
  const bgLoadingRef = useRef(false);
  // Track the total expected schools to calculate progress
  const totalSchoolsRef = useRef(0);

  // Define loadFreshSchoolsInBackground with useCallback first
  const loadFreshSchoolsInBackground = useCallback(async () => {
    if (bgLoadingRef.current) return; // Prevent multiple parallel background loads
    
    try {
      bgLoadingRef.current = true;
      setLoadingStatus('Refreshing data in background...');
      
      // Add timeout to prevent infinite loading on refresh
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Background refresh timeout')), 30000) // 30 second timeout
      );
      
      // Race the API call against the timeout
      const response = await Promise.race([
        schoolsApi.getAllSchools(null, { paginated: false }),
        timeoutPromise
      ]);
      
      const freshSchools = Array.isArray(response) ? response : (response.schools || []);
      
      totalSchoolsRef.current = freshSchools.length;
      
      // Update schools state with fresh data
      setSchools(freshSchools);
      // Note: We don't have teams metadata from this endpoint anymore
      setTeamsMetadata({ teams: [], team_groups: [] });
      
      setLoadingStatus('Data refreshed');
      setLoadingProgress(100);
    } catch (err) {
      console.error('Background refresh failed:', err);
      setLoadingStatus('Background refresh failed');
      setLoadingProgress(100);
      // Don't update error state to avoid disrupting the UI
    } finally {
      bgLoadingRef.current = false;
    }
  }, []);

  // Teams-based school loading with two-phase approach
  const loadSchools = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingStatus('Initializing...');
      setLoadingProgress(10);
      
      setLoadingStatus('Loading your schools...');
      setLoadingProgress(30);
      
      try {
        // UPDATED: Use the direct schools endpoint that uses OptimizedOrganizationalCache
        const response = await schoolsApi.getAllSchools(null, {
          paginated: true,
          page: 1,
          page_size: 100
        });
        
        // Handle both array response and object response formats
        const initialSchools = Array.isArray(response) ? response : (response.schools || []);
        
        if (initialSchools && initialSchools.length > 0) {
          // Update initial state and progress
          setSchools(initialSchools);
          setTeamsMetadata({ teams: [], team_groups: [] });
          setLoadingProgress(70);
          
          // Store total number for progress tracking
          totalSchoolsRef.current = response.total || initialSchools.length;
          
          // Mark loading complete to make UI interactive
          setLoading(false);
          setLoadingStatus('Loaded initial schools');
          
          // SECOND PHASE: Load all schools in background if there are more
          if (response.total && response.total > initialSchools.length) {
            bgLoadingRef.current = true;
            
            // Delay slightly to allow UI to become responsive
            setTimeout(async () => {
              try {
                setLoadingStatus('Loading all schools...');
                
                // Add timeout to prevent infinite loading
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Background loading timeout')), 30000) // 30 second timeout
                );
                
                // Race the API call against the timeout
                const fullResponse = await Promise.race([
                  schoolsApi.getAllSchools(null, { paginated: false }),
                  timeoutPromise
                ]);
                
                const allSchools = Array.isArray(fullResponse) ? fullResponse : (fullResponse.schools || []);
                
                if (allSchools && allSchools.length > 0) {
                  setSchools(allSchools);
                  totalSchoolsRef.current = allSchools.length;
                  setLoadingStatus('All schools loaded');
                  setLoadingProgress(100);
                } else {
                  setLoadingStatus('Background loading completed');
                  setLoadingProgress(100);
                }
              } catch (err) {
                console.error('Background loading failed:', err);
                setLoadingStatus('Background loading failed');
                setLoadingProgress(100);
                // Don't set error state to avoid disrupting UI, but ensure loading stops
              } finally {
                bgLoadingRef.current = false;
              }
            }, 500);
          } else {
            setLoadingProgress(100);
          }
        } else {
          // No schools returned
          setError('No schools available for your account.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading schools:', err);
        setError('Failed to load schools. Please try again.');
        setLoading(false);
        // Ensure background loading is also stopped on error
        bgLoadingRef.current = false;
      }
    } catch (err) {
      setError(err.message);
      setLoadingStatus('Error loading schools');
      setLoading(false);
      // Ensure background loading is also stopped on error
      bgLoadingRef.current = false;
    }
  }, []);

  // Publicly exposed refresh function
  const refreshTeamSchools = useCallback(() => {
    if (bgLoadingRef.current) return; // Don't start if already loading
    loadFreshSchoolsInBackground();
  }, [loadFreshSchoolsInBackground]);

  // Load schools when component mounts or user changes
  useEffect(() => {
    if (user?.uid) {
      loadSchools();
    }
  }, [loadSchools, user?.uid]);

  return {
    schools,
    loading,
    error,
    loadingStatus,
    loadingProgress,
    refreshTeamSchools,
    teamsMetadata,
    isBackgroundLoading: bgLoadingRef.current,
    totalSchoolsCount: totalSchoolsRef.current
  };
}